import AssetBackedFinance from 'assets/svgs/productTypes/assetBackedFinance.svg';
import InvoiceFinancing from 'assets/svgs/productTypes/invoiceFinancing.svg';
import MerchantCashAdvance from 'assets/svgs/productTypes/merchantCashAdvance.svg';
import TermsLoans from 'assets/svgs/productTypes/termsLoans.svg';
import TradeFinance from 'assets/svgs/productTypes/tradeFinance.svg';
import CommercialProperty from 'assets/svgs/productTypes/commercialProperty.svg';
import {
  IconBank,
  IconTruck,
  IconStore,
  IconRequestQuote,
  IconBriefcase,
  IconHouse,
} from '@capitalise/ui-design-system';

export const ProductType = Object.freeze({
  termLoans: 1,
  fixedLoanTerms: 2,
  assetBackedFinance: 4,
  merchantCashAdvance: 8,
  invoiceFinancing: 16,
  ecommerceFunding: 32,
  tradeFinance: 64,
  commercialProperty: 128,
  debtRecovery: 201,
  researchAndDevelopmentTax: 202,
  capitalAllowances: 203,
  creditImprovement: 204,
  creditReview: 206,
  grants: 205,
  governmentBacked: -1,
  recoveryLoanScheme: 20,
});

export const ProductTypeKeyString = Object.freeze({
  [ProductType.termLoans]: 'TERM_LOAN',
  [ProductType.fixedLoanTerms]: 'FIXED_LOAN_TERMS',
  [ProductType.assetBackedFinance]: 'ASSET_BACKED_FINANCE',
  [ProductType.merchantCashAdvance]: 'MERCHANT_CASH_ADVANCE',
  [ProductType.invoiceFinancing]: 'INVOICE_FINANCE',
  [ProductType.ecommerceFunding]: 'E_COMMERCE_FUNDING',
  [ProductType.tradeFinance]: 'TRADE_FINANCE',
  [ProductType.commercialProperty]: 'COMMERCIAL_PROPERTY',
});

export const ProductTypeKey = Object.freeze({
  ASSET_BACKED_FINANCE: ProductType.assetBackedFinance,
  INVOICE_FINANCE: ProductType.invoiceFinancing,
  TRADE_FINANCE: ProductType.tradeFinance,
  MERCHANT_CASH_ADVANCE: ProductType.merchantCashAdvance,
  TERM_LOAN: ProductType.termLoans,
  COMMERCIAL_PROPERTY: ProductType.commercialProperty,
});

export const ProductTypeTitle = Object.freeze({
  [ProductType.assetBackedFinance]: 'asset-backed-finance',
  [ProductType.invoiceFinancing]: 'invoice-finance',
  [ProductType.tradeFinance]: 'trade-finance',
  [ProductType.merchantCashAdvance]: 'merchant-cash-advance',
  [ProductType.termLoans]: 'term-loans',
  [ProductType.commercialProperty]: 'commercial-property',
});

export const ProductKey = Object.freeze({
  [ProductType.assetBackedFinance]: 'ASSET_BACKED_FINANCE',
  [ProductType.invoiceFinancing]: 'INVOICE_FINANCE',
  [ProductType.tradeFinance]: 'TRADE_FINANCE',
  [ProductType.merchantCashAdvance]: 'MERCHANT_CASH_ADVANCE',
  [ProductType.termLoans]: 'TERM_LOAN',
  [ProductType.commercialProperty]: 'COMMERCIAL_PROPERTY',
});

export const ProductTypeDataName = Object.freeze({
  [ProductType.assetBackedFinance]: 'assetFinance',
  [ProductType.commercialProperty]: 'commercialProperty',
  [ProductType.invoiceFinancing]: 'invoiceFinance',
  [ProductType.tradeFinance]: 'tradeFinance',
  [ProductType.merchantCashAdvance]: 'merchantCashAdvance',
  [ProductType.termLoans]: 'termLoan',
});

export const ProductTypeRequiredFields = Object.freeze({
  [ProductType.assetBackedFinance]: ['productOption', 'advanceAmount'],
  [ProductType.invoiceFinancing]: ['maximumCreditLine', 'advanceRate'],
  [ProductType.tradeFinance]: ['maximumCreditLine', 'advanceRate'],
  [ProductType.merchantCashAdvance]: ['fundingAmount', 'sweep'],
  [ProductType.termLoans]: ['representativeAPR', 'likelihoodOfAcceptance', 'maximumAmount'],
});

export const ProductTypeIcon = Object.freeze({
  [ProductType.assetBackedFinance]: AssetBackedFinance,
  [ProductType.invoiceFinancing]: InvoiceFinancing,
  [ProductType.merchantCashAdvance]: MerchantCashAdvance,
  [ProductType.termLoans]: TermsLoans,
  [ProductType.tradeFinance]: TradeFinance,
  [ProductType.commercialProperty]: CommercialProperty,
});

export const ProductTypeIconNewDesignSystem = Object.freeze({
  [ProductType.assetBackedFinance]: IconTruck,
  [ProductType.invoiceFinancing]: IconRequestQuote,
  [ProductType.merchantCashAdvance]: IconStore,
  [ProductType.termLoans]: IconBank,
  [ProductType.tradeFinance]: IconBriefcase,
  [ProductType.commercialProperty]: IconHouse,
});

export const ProductTypeLabel = Object.freeze({
  [ProductType.assetBackedFinance]: 'Asset Backed Finance',
  [ProductType.ecommerceFunding]: 'E-commerce Funding',
  [ProductType.fixedLoanTerms]: 'Fixed Loan Terms',
  [ProductType.invoiceFinancing]: 'Invoice Finance',
  [ProductType.merchantCashAdvance]: 'Merchant Cash Advance',
  [ProductType.termLoans]: 'Term Loans',
  [ProductType.tradeFinance]: 'Trade Finance',
  [ProductType.commercialProperty]: 'Property Finance',
  [ProductType.debtRecovery]: 'Debt Recovery',
  [ProductType.researchAndDevelopmentTax]: 'R&D Tax',
  [ProductType.capitalAllowances]: 'Capital Allowances',
  [ProductType.creditImprovement]: 'Credit Improvement',
  [ProductType.creditReview]: 'Credit Review With Experian',
  [ProductType.grants]: 'Grants',
  [ProductType.recoveryLoanScheme]: 'Recovery Loan Scheme',
});

export const QuesionnaireProductTypeMapping = Object.freeze({
  [ProductType.invoiceFinancing]: 'invoices',
  [ProductType.assetBackedFinance]: 'asset',
  [ProductType.merchantCashAdvance]: 'epos',
  [ProductType.tradeFinance]: 'purchaseOrders',
  [ProductType.commercialProperty]: 'purchaseDevelopProperty',
  [ProductType.governmentBacked]: 'governmentBacked',
});

export const ClientType = Object.freeze({
  SMALL_SMES: 1,
  LARGE_CORPORATES: 2,
  PUBLIC_SECTOR: 3,
  CONSUMER: 4,
});

const getDefaultProductTypesMap = () => {
  const map = {};
  Object.values(QuesionnaireProductTypeMapping).forEach((productType) => {
    map[productType] = false;
  });

  return map;
};

export const mapInsightProductTypesToAnswers = (productTypes) => {
  const result = getDefaultProductTypesMap();
  productTypes.forEach((type) => {
    if (QuesionnaireProductTypeMapping[type]) {
      result[QuesionnaireProductTypeMapping[type]] = true;
    }
  });
  return result;
};

export const ProductTypeNarrative = Object.freeze({
  [ProductType.assetBackedFinance]:
    'Cost effective funding to invest in or refinance business equipment by spreading the cost over an agreed period of time.',
  [ProductType.commercialProperty]:
    'Finance secured by way of 1st charges against properties, whether long or short term; residential, commercial or mixed-use; additional properties or portfolio refinances; refurbs or ground up developments',
  [ProductType.invoiceFinancing]:
    'Popular option for businesses seeking to raise funding quickly and effectively by advancing a percentage of their receivables.',
  [ProductType.merchantCashAdvance]:
    'Raise funding against future credit card sales. A merchant cash advance offers short payment terms with small, regular payments.',
  [ProductType.termLoans]:
    'Multi-purpose loan designed for daily operational costs from staff wages to paying rent. An easy and hassle-free way to secure funding.',
  [ProductType.tradeFinance]:
    'Fund suppliers upfront based on confirmed orders and finished goods. A proven method to increase business and trade with confidence.',
});

export const ProductTypeFeatures = Object.freeze({
  [ProductType.assetBackedFinance]: [
    'Typically cheaper than unsecured loans',
    '5yr+ terms available',
    'Personal guarantees not necessarily required',
  ],
  [ProductType.commercialProperty]: [
    'Debt secured against residential, commercial & mixed-use property portfolios; land with planning or ground up developments',
    'Facilities available on fixed terms from 12 months to 30 years',
    'LTVs up to 75% available',
    '2nd charges & mezzanine funding also available on case-by-case basis',
  ],
  [ProductType.invoiceFinancing]: [
    'Flexible on security required',
    'Adhoc facilities or 12 month contracts',
    'Large range of product offerings (discounting, factoring and selective)',
  ],
  [ProductType.merchantCashAdvance]: [
    'High approval rate (typically 80%)',
    'Fixed cost that does not fluctuate',
    'Completely flexible repayments',
  ],
  [ProductType.termLoans]: [
    'Typical repayment periods between 12 - 60 months',
    'Secured by a personal guarantee, and/or a property charge',
    'Rates typically between 3% - 30%',
  ],
  [ProductType.tradeFinance]: [
    'Cover upfront supplier costs',
    'Security required',
    'Flexible credit line when purchase orders are issued',
  ],
});

export const ProductTypeBestSuited = Object.freeze({
  [ProductType.assetBackedFinance]: [
    'Purchasing new assets',
    'Releasing working capital from unencumbered business assets',
    'SMEs that are not strong enough to borrow unsecured',
  ],
  [ProductType.commercialProperty]: [
    'Purchasing a new property within a Ltd company',
    'Refinancing an existing portfolio',
    'Ground up developments for experienced developers',
  ],
  [ProductType.invoiceFinancing]: [
    'SMEs with long credit terms (typically 30 - 120 days)',
    'SMEs with debtors which struggle to pay in a timely manner',
    'Short term cashflow/funding',
  ],
  [ProductType.merchantCashAdvance]: [
    'SMEs who operate seasonly',
    'Merchants with an EPOS system',
    'Sits aside other funding products',
  ],
  [ProductType.termLoans]: [
    'Medium to long term investment projects, such as hiring new staff',
    'SMEs trading for 2 or more years, with a fair level of net profit',
    'SMEs with positive net assets',
  ],
  [ProductType.tradeFinance]: [
    'SMEs with large purchase orders',
    'SMEs with suppliers located overseas',
    'SMEs who purchase finished goods',
  ],
});

export const ProductTypeCost = Object.freeze({
  [ProductType.assetBackedFinance]: {
    description: 'APR',
    value: '6 - 15%',
  },
  [ProductType.commercialProperty]: {
    description: 'APR',
    value: '2-10%',
  },
  [ProductType.invoiceFinancing]: {
    description: 'Per Invoice',
    value: '1 - 4%',
  },
  [ProductType.merchantCashAdvance]: {
    description: 'Factor Rate',
    value: '1.09 - 1.35',
  },
  [ProductType.termLoans]: {
    description: 'APR',
    value: '6 - 18%',
  },
  [ProductType.tradeFinance]: {
    description: 'Per Invoice',
    value: '1 - 4%',
  },
});

export function toOptions() {
  return Object.entries(ProductTypeLabel).map(([value, label]) => ({ label, value }));
}

export const ProductCapitalType = Object.freeze({
  raise: 'raise',
  recoverAndProtect: 'recover & protect',
});

export const ProductCapitalLabel = Object.freeze({
  [ProductCapitalType.raise]: 'Raise Capital',
  [ProductCapitalType.recoverAndProtect]: 'Recover & Protect Capital',
});
